/**
 * Org-wide language configuration.
 *
 * Stored as four keys in `OrganizationFieldValues` — the same store `PAYROLL_MIN_NET_PAY` uses —
 * reached through the already-wired `getOrgFieldValues` / `saveOrgFieldValues`. **No migration**:
 * the table, the endpoints and the SPA's upsert all exist.
 *
 * Design: docs/43-I18N-ARABIC-DESIGN.md §8.
 */

import { getOrgFieldValues, saveOrgFieldValues } from '../services/localizationService';
import {
  DEFAULT_LOCALE,
  LOCALE_CODES,
  isLocaleCode,
  resolveLocaleTag,
  type LocaleCode,
} from './locales';

export const ORG_LOCALE_KEYS = {
  defaultLocale: 'ORG_DEFAULT_LOCALE',
  enabledLocales: 'ORG_ENABLED_LOCALES',
  allowUserOverride: 'ORG_ALLOW_USER_LOCALE_OVERRIDE',
  showHijriDates: 'ORG_SHOW_HIJRI_DATES',
} as const;

/** Where a personal override lives. Per-device: there is no `PreferredLanguage` column in UM. */
export const USER_LOCALE_STORAGE_KEY = 'hrms.locale';

/**
 * The last-known effective locale for this device, cached so **boot** can resolve the right
 * language before the app's module graph is evaluated.
 *
 * Org settings need auth, so they arrive long after import time — but module-scope constants that
 * call `t()` (antd column definitions, the sidebar's `MODULE_META`, status maps) are frozen at
 * import. Without this cache the first paint after every reload would be English for an
 * Arabic-default org, and those constants would stay English for the whole session.
 *
 * It is a cache, not a source of truth: `applyOrgSettings` overwrites it from the server's answer
 * on every sign-in, so an org that changes its default corrects the device on the next load.
 */
export const ORG_LOCALE_CACHE_KEY = 'hrms.orgLocale';

export interface OrgLocaleSettings {
  defaultLocale: LocaleCode;
  enabledLocales: LocaleCode[];
  allowUserOverride: boolean;
  showHijriDates: boolean;
}

/**
 * What an org gets before anyone configures it: English only, overrides allowed, no Hijri.
 *
 * English-only matters — an org that has never opted in must not suddenly find its staff looking
 * at a half-translated Arabic UI because the feature shipped.
 */
export const DEFAULT_ORG_LOCALE_SETTINGS: OrgLocaleSettings = {
  defaultLocale: DEFAULT_LOCALE,
  enabledLocales: [DEFAULT_LOCALE],
  allowUserOverride: true,
  showHijriDates: false,
};

const parseBoolean = (raw: string | undefined, fallback: boolean): boolean => {
  if (raw === undefined || raw === null || raw === '') return fallback;
  return ['true', '1', 'yes'].includes(raw.trim().toLowerCase());
};

const parseEnabledLocales = (raw: string | undefined): LocaleCode[] => {
  if (!raw) return [DEFAULT_LOCALE];
  const parsed = raw
    .split(',')
    .map((part) => resolveLocaleTag(part))
    .filter((c): c is LocaleCode => c !== null);

  const unique = Array.from(new Set(parsed));
  // English is always available. An org that removes it — by typo or by intent — would otherwise
  // strand every user whose language is not in the list, including the admin fixing the mistake.
  if (!unique.includes(DEFAULT_LOCALE)) unique.unshift(DEFAULT_LOCALE);
  return unique;
};

/** Turn raw `OrganizationFieldValues` rows into settings, tolerating absent and malformed values. */
export const parseOrgLocaleSettings = (
  values: Array<{ fieldKey: string; fieldValue?: string | null }>,
): OrgLocaleSettings => {
  const map = new Map(values.map((v) => [v.fieldKey, v.fieldValue ?? undefined]));

  const enabledLocales = parseEnabledLocales(map.get(ORG_LOCALE_KEYS.enabledLocales));
  const rawDefault = map.get(ORG_LOCALE_KEYS.defaultLocale);
  const resolvedDefault = resolveLocaleTag(rawDefault ?? null);

  return {
    // A default the org has not actually enabled is a misconfiguration, not an instruction —
    // fall back rather than showing a language the admin thinks is switched off.
    defaultLocale:
      resolvedDefault && enabledLocales.includes(resolvedDefault) ? resolvedDefault : DEFAULT_LOCALE,
    enabledLocales,
    allowUserOverride: parseBoolean(
      map.get(ORG_LOCALE_KEYS.allowUserOverride),
      DEFAULT_ORG_LOCALE_SETTINGS.allowUserOverride,
    ),
    showHijriDates: parseBoolean(
      map.get(ORG_LOCALE_KEYS.showHijriDates),
      DEFAULT_ORG_LOCALE_SETTINGS.showHijriDates,
    ),
  };
};

export const fetchOrgLocaleSettings = async (orgId: number): Promise<OrgLocaleSettings> => {
  try {
    const values = await getOrgFieldValues(orgId);
    return parseOrgLocaleSettings(values ?? []);
  } catch {
    // Language configuration must never be able to block sign-in. An org whose settings cannot be
    // read gets English, which is the pre-feature behaviour.
    return DEFAULT_ORG_LOCALE_SETTINGS;
  }
};

export const saveOrgLocaleSettings = async (
  orgId: number,
  settings: OrgLocaleSettings,
): Promise<void> => {
  await saveOrgFieldValues(orgId, {
    [ORG_LOCALE_KEYS.defaultLocale]: settings.defaultLocale,
    [ORG_LOCALE_KEYS.enabledLocales]: settings.enabledLocales.join(','),
    [ORG_LOCALE_KEYS.allowUserOverride]: String(settings.allowUserOverride),
    [ORG_LOCALE_KEYS.showHijriDates]: String(settings.showHijriDates),
  });
};

// ── Personal override ────────────────────────────────────────────────────────

export const readStoredUserLocale = (): LocaleCode | null => {
  try {
    const raw = localStorage.getItem(USER_LOCALE_STORAGE_KEY);
    return isLocaleCode(raw) ? raw : null;
  } catch {
    return null;
  }
};

export const readCachedOrgLocale = (): LocaleCode | null => {
  try {
    const raw = localStorage.getItem(ORG_LOCALE_CACHE_KEY);
    return isLocaleCode(raw) ? raw : null;
  } catch {
    return null;
  }
};

export const writeCachedOrgLocale = (locale: LocaleCode): void => {
  try {
    localStorage.setItem(ORG_LOCALE_CACHE_KEY, locale);
  } catch {
    /* private browsing — the app still works, boot just falls back to English once */
  }
};

/**
 * The locale to boot with, before any server call has happened.
 *
 * Order: the user's own choice → the org's last-known default for this device → the browser.
 */
export const bootLocale = (browserLanguage?: string | null): LocaleCode =>
  resolveEffectiveLocale(null, readStoredUserLocale() ?? readCachedOrgLocale(), browserLanguage);

export const writeStoredUserLocale = (locale: LocaleCode | null): void => {
  try {
    if (locale === null) localStorage.removeItem(USER_LOCALE_STORAGE_KEY);
    else localStorage.setItem(USER_LOCALE_STORAGE_KEY, locale);
  } catch {
    // Private browsing / disabled storage. The session still works, the choice just does not stick.
  }
};

/**
 * Resolve the locale actually to be used: personal override → org default → English.
 *
 * The two guards matter more than the order:
 *
 * - A stored override is **discarded when the org has since disabled that language**, or when the
 *   org has turned overrides off. Otherwise a user who once picked Arabic keeps seeing it after
 *   the org withdraws it, and no admin action can reach them.
 * - `browserLanguage` is consulted **only before sign-in**, where no org settings exist yet. After
 *   sign-in the org's configuration is authoritative, so a visitor with an Arabic browser does not
 *   override an English-only tenant.
 */
export const resolveEffectiveLocale = (
  settings: OrgLocaleSettings | null,
  storedUserLocale: LocaleCode | null,
  browserLanguage?: string | null,
): LocaleCode => {
  if (!settings) {
    const stored = storedUserLocale;
    if (stored) return stored;
    const fromBrowser = resolveLocaleTag(browserLanguage ?? null);
    return fromBrowser ?? DEFAULT_LOCALE;
  }

  if (
    settings.allowUserOverride &&
    storedUserLocale &&
    settings.enabledLocales.includes(storedUserLocale)
  ) {
    return storedUserLocale;
  }

  return settings.defaultLocale;
};

/** The languages a user may actually pick. Empty-able to a single entry, which hides the switcher. */
export const selectableLocales = (settings: OrgLocaleSettings | null): LocaleCode[] => {
  if (!settings) return [...LOCALE_CODES];
  if (!settings.allowUserOverride) return [settings.defaultLocale];
  return settings.enabledLocales;
};
